import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Chip, Flex, FlexSpace } from '@elementary/common-ui/components';
import OverflowTooltip from '@elementary/common-ui/components/OverflowTooltip';
import useHover from '@elementary/common-ui/hooks/useHover';
import { Position, NodeProps, useEdges } from 'reactflow';
import { GRAPH_CHILD_NODE_HEIGHT } from './constants';
import { ReactFlowNodeData } from './types';
import { useLineageGraphContext } from './LineageGraphContext';
import GraphNodeHandle from './GraphNodeHandle';
import NodeContextMenu from './NodeContextMenu';

const Wrapper = styled(Flex)<{
  $isHighlighted?: boolean;
  $isLastChild?: boolean;
  $isOnHighlightPath?: boolean;
  $width: number;
}>`
  height: ${GRAPH_CHILD_NODE_HEIGHT}px;
  width: ${({ $width }) => $width}px;
  cursor: pointer;

  border: ${({ theme, $isHighlighted, $isOnHighlightPath }) =>
    $isHighlighted || $isOnHighlightPath
      ? `2px solid ${theme.palette.common.blue7}`
      : 'none'};

  padding: ${({ $isHighlighted, $isOnHighlightPath }) =>
    $isHighlighted || $isOnHighlightPath ? '0' : '0 2px'};

  padding-bottom: ${({ $isHighlighted, $isOnHighlightPath, $isLastChild }) =>
    $isHighlighted || $isOnHighlightPath || !$isLastChild ? '0' : '2px'};

  border-radius: ${({ $isLastChild }) => ($isLastChild ? '0 0 6px 6px' : 0)};
`;

const InnerWrapper = styled(Flex)<{
  $isHighlighted?: boolean;
  $isLastChild?: boolean;
  $isOnHighlightPath?: boolean;
  $isRootNode?: boolean;
  $isMenuOpen?: boolean;
}>`
  width: 100%;
  height: 100%;

  padding: ${({ $isHighlighted, $isLastChild, $isOnHighlightPath }) =>
    !($isHighlighted || $isOnHighlightPath) && $isLastChild
      ? '2px 16px 0 16px'
      : '0 16px'};

  border-radius: ${({ $isLastChild }) => ($isLastChild ? '0 0 4px 4px' : 0)};

  background: ${({ theme, $isHighlighted, $isRootNode, $isMenuOpen }) =>
    $isMenuOpen
      ? theme.palette.common.gray10
      : $isRootNode
      ? theme.palette.common.lightMagenta
      : $isHighlighted
      ? theme.palette.common.gray9
      : theme.palette.common.white};

  &:hover {
    background: ${({ theme }) => theme.palette.common.gray10};
  }
`;

const GraphChildNode = (node: NodeProps<ReactFlowNodeData>) => {
  const { id, data, dragging } = node;
  const edges = useEdges();

  const hasIncomingEdges = edges.some((e) => e.target === id && !e.hidden);
  const hasOutgoingEdges = edges.some((e) => e.source === id && !e.hidden);

  const { highlightedNode, isControlled } = useLineageGraphContext();

  const ref = useRef<any>();
  const isHover = useHover(ref);

  const [menuPos, setMenuPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const handleMenuOpen = (e: any) => {
    if (isControlled) return;
    e.stopPropagation();
    e.preventDefault();
    setMenuPos({ left: e.clientX, top: e.clientY });
  };

  const handleMenuClose = () => {
    setMenuPos(null);
  };

  const isLastChild = useMemo(() => {
    const allSiblings = data.parentNode?.children;
    if (allSiblings) {
      return id === allSiblings[allSiblings.length - 1].id;
    }
    return false;
  }, [data, id]);

  const isChildOfChildNode = !!node.data.parentNode?.parent;

  return (
    <Wrapper
      ref={ref}
      $isHighlighted={highlightedNode?.id === id}
      $isLastChild={isLastChild}
      $isOnHighlightPath={data.isOnHighlightPath}
      $width={data.width}
      onContextMenu={isControlled ? undefined : handleMenuOpen}
    >
      <InnerWrapper
        $isHighlighted={highlightedNode?.id === id}
        $isLastChild={isLastChild}
        $isOnHighlightPath={data.isOnHighlightPath}
        $isRootNode={data.isRootNode}
        $isMenuOpen={!!menuPos}
      >
        <GraphNodeHandle
          type="source"
          position={Position.Right}
          $extendHandle={hasOutgoingEdges && isChildOfChildNode}
          $isHighlighted={id === highlightedNode?.id || data.isOnHighlightPath}
          $nodeHeight={GRAPH_CHILD_NODE_HEIGHT}
        />

        <FlexSpace
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <OverflowTooltip
            sx={{
              fontSize: '14px',
            }}
            color={
              highlightedNode?.id === id ? 'common.magenta' : 'common.black'
            }
            bold={highlightedNode?.id === id}
          >
            {data.metadata.label}
          </OverflowTooltip>

          {(!!data.totals?.errors ||
            !!data.totals?.warnings ||
            !!data.totals?.passed ||
            !!data.totals?.failures) && (
            <Flex sx={{ alignItems: 'center', gap: 0.6, ml: 3 }}>
              {data.totals?.passed > 0 && (
                <Chip
                  role="alert"
                  aria-label="success"
                  label={data.totals.passed}
                  color="success"
                  size="small"
                />
              )}
              {data.totals?.warnings > 0 && (
                <Chip
                  role="alert"
                  aria-label="warning"
                  label={data.totals.warnings}
                  color="warning"
                  size="small"
                />
              )}
              {(data.totals?.errors > 0 || data.totals?.failures > 0) && (
                <Chip
                  role="alert"
                  aria-label="error"
                  label={
                    (data.totals.errors || 0) + (data.totals.failures || 0)
                  }
                  color="error"
                  size="small"
                />
              )}
            </Flex>
          )}
        </FlexSpace>

        {!isControlled && (
          <NodeContextMenu
            showMenuButton={isHover && !dragging}
            node={node}
            pos={menuPos}
            onOpen={handleMenuOpen}
            onClose={handleMenuClose}
            onHighlightNodeClick={() => ref.current?.click()}
          />
        )}

        <GraphNodeHandle
          type="target"
          position={Position.Left}
          $extendHandle={hasIncomingEdges && isChildOfChildNode}
          $isHighlighted={id === highlightedNode?.id || data.isOnHighlightPath}
          $nodeHeight={GRAPH_CHILD_NODE_HEIGHT}
        />
      </InnerWrapper>
    </Wrapper>
  );
};

export default GraphChildNode;
